import { useEffect, useState } from 'react'

import apis from 'browser/app/models/apis'

const BASE = '/1.0/entities/actions/metrics/prometheus/api/v1'

interface IPromSample {
  metric: Record<string, string>
  value?: [number, string]
}

type IPromResult =
  | { resultType: 'vector'; result: IPromSample[] }
  | { resultType: 'scalar'; result: [number, string] }
  | { resultType: 'matrix' | 'string'; result: unknown }

interface IPromResponse {
  status: string
  data?: IPromResult
}

export interface IMetricData {
  isLoading: boolean
  error?: string
  isEmpty: boolean
  stat?: number
}

/**
 * Runs an instant query against the per-firm metrics passthrough and sums the result vector, since
 * counters fan out per server instance; a scalar result is shown as-is. The firm is enforced
 * server-side, so the query carries no tenant. Re-runs every `refreshMs` (0 disables), keeping the
 * last value on screen while it reloads.
 */
export function useMetricData(query?: string, refreshMs = 0): IMetricData {
  const [state, setState] = useState<IMetricData>({ isLoading: false, isEmpty: false })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!query || refreshMs <= 0) {
      return
    }
    const interval = setInterval(() => setTick((t) => t + 1), refreshMs)
    return () => clearInterval(interval)
  }, [query, refreshMs])

  useEffect(() => {
    setState({ isLoading: Boolean(query), isEmpty: !query })
  }, [query])

  useEffect(() => {
    if (!query) {
      return
    }
    let cancelled = false

    // No retries: the next refresh is the retry, and getJSON's default would hold a tile on a
    // failing backend for minutes.
    const request = apis.getJSON<IPromResponse>(
      `${apis.getHost()}${BASE}/query?query=${encodeURIComponent(query)}`,
      { retry: { maxRetries: 0 } }
    )
    request
      .then((response) => {
        if (cancelled) {
          return
        }
        const data = response.data
        if (data && data.resultType !== 'vector' && data.resultType !== 'scalar') {
          setState({
            isLoading: false,
            isEmpty: false,
            error: `Unsupported result type '${data.resultType}'`,
          })
          return
        }
        let values: string[] = []
        if (data?.resultType === 'scalar') {
          values = [data.result[1]]
        } else if (data?.resultType === 'vector') {
          values = data.result.map((s) => s.value?.[1] ?? '0')
        }
        const stat = values.reduce((sum, v) => sum + Number(v), 0)
        // Prometheus encodes NaN/±Inf as strings (e.g. a ratio over a zero denominator); show those as
        // no data rather than letting numeral render them as 0.
        if (values.length === 0 || !Number.isFinite(stat)) {
          setState({ isLoading: false, isEmpty: true })
          return
        }
        setState({ isLoading: false, isEmpty: false, stat })
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ isLoading: false, isEmpty: false, error: error?.message ?? 'Failed to load' })
        }
      })

    return () => {
      cancelled = true
      request.cancel()
    }
  }, [query, tick])

  return state
}
